import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../app/OrgContext.js';
import { currencySymbol } from '@goss/types';
import { apiFetch } from '../lib/api.js';
import { C, alpha } from '../lib/theme.js';
import { Card, PageHeader } from '../ui/ui.js';
import { Overview } from './Overview.js';
import type { MetricRow, MetricsResponse, RoasReport } from '../lib/types.js';

/* Results — the bottom line: are ads producing bookings, and what to do next.
   Attention metrics (RULES §6) diagnose; bookings + ROAS (RULES §7) decide.
   Attribution is honest/partial — the coverage caveat is always shown. */

function money(n: number, sym: string): string {
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sym}${(n / 1_000).toFixed(1)}k`;
  return `${sym}${Math.round(n).toLocaleString()}`;
}

interface Attn {
  spend: number;
  impressions: number;
  clicks: number;
  hook: number | null;
  freq: number | null;
  ctr: number | null;
}
type Verdict = 'SCALE' | 'KEEP' | 'ROTATE' | 'KILL' | 'EARLY';
interface Row {
  id: string;
  name: string;
  spend: number;
  bookings: number;
  revenue: number;
  roas: number | null;
  costPerBooking: number | null;
  attn: Attn | null;
  verdict: Verdict;
  why: string;
}

// RULES §6 kill/keep/scale, expressed against what we can see. Bookings/ROAS lead;
// attention (hook/CTR/frequency) fills in when conversion data is still thin.
function decide(
  spend: number,
  bookings: number,
  roas: number | null,
  a: Attn | null,
): {
  verdict: Verdict;
  why: string;
} {
  const hook = a?.hook ?? null;
  const freq = a?.freq ?? null;
  // Booking-led verdicts first (the real signal).
  if (bookings > 0) {
    if (roas != null && roas >= 1.5)
      return { verdict: 'SCALE', why: `Profitable at ${roas.toFixed(1)}x — scale +20%.` };
    if (roas != null && roas >= 1)
      return {
        verdict: 'KEEP',
        why: `Breaking even at ${roas.toFixed(1)}x — leave it, watch the trend.`,
      };
    return {
      verdict: 'KEEP',
      why: `Booking, but ROAS under 1x — tighten targeting or offer before scaling.`,
    };
  }
  // No bookings yet.
  if (spend < 700)
    return { verdict: 'EARLY', why: 'Too little spend to judge — give it to day 5+.' };
  if (freq != null && freq > 3)
    return {
      verdict: 'ROTATE',
      why: `Frequency ${freq.toFixed(1)} — audience fatigued, rotate the creative.`,
    };
  if (hook != null && hook < 20)
    return {
      verdict: 'KILL',
      why: `Weak hook (${hook.toFixed(0)}%) and no bookings after real spend.`,
    };
  if (hook != null && hook >= 30)
    return {
      verdict: 'KEEP',
      why: `Strong hook (${hook.toFixed(0)}%) but no bookings yet — check the landing page / offer.`,
    };
  return {
    verdict: 'KEEP',
    why: 'Spending with no bookings yet — watch closely over the next few days.',
  };
}

const VERDICT_LOOK: Record<Verdict, { color: string; label: string }> = {
  SCALE: { color: C.good, label: 'SCALE' },
  KEEP: { color: C.info, label: 'KEEP' },
  ROTATE: { color: C.watch, label: 'ROTATE' },
  KILL: { color: C.act, label: 'KILL' },
  EARLY: { color: C.faint, label: 'TOO EARLY' },
};

export function Results() {
  const { orgId, activeOrg } = useOrg();
  const navigate = useNavigate();
  const [roas, setRoas] = useState<RoasReport | null>(null);
  const [metrics, setMetrics] = useState<MetricRow[] | null>(null);
  const [thumbBy, setThumbBy] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!orgId) return;
    setRoas(null);
    setMetrics(null);
    setError(null);
    Promise.all([
      apiFetch<RoasReport>(`/api/orgs/${orgId}/roas`),
      apiFetch<MetricsResponse>(`/api/orgs/${orgId}/metrics?level=campaign&days=${days}`),
    ])
      .then(([r, m]) => {
        setRoas(r);
        setMetrics(m.rows);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load results'));
  }, [orgId, days]);

  // Each Meta campaign's ad thumbnail, straight from Meta (one call over the account's
  // ads) — covers both Basil-built and amplified campaigns, no cache dependency.
  useEffect(() => {
    if (!orgId) return;
    apiFetch<{ thumbs: Record<string, string> }>(`/api/orgs/${orgId}/campaign-thumbs`)
      .then((d) => setThumbBy(d.thumbs ?? {}))
      .catch(() => {});
  }, [orgId]);

  // Attention per campaign (spend/hook/ctr/freq) aggregated from metrics_daily.
  const attnBy = useMemo(() => {
    const m = new Map<string, Attn & { _hs: number; _hn: number; _fs: number; _fn: number }>();
    for (const r of metrics ?? []) {
      const id = r.entity_id;
      let a = m.get(id);
      if (!a) {
        a = {
          spend: 0,
          impressions: 0,
          clicks: 0,
          hook: null,
          freq: null,
          ctr: null,
          _hs: 0,
          _hn: 0,
          _fs: 0,
          _fn: 0,
        };
        m.set(id, a);
      }
      a.spend += Number(r.spend) || 0;
      a.impressions += Number(r.impressions) || 0;
      a.clicks += Number(r.clicks) || 0;
      if (r.hook_rate != null) {
        a._hs += r.hook_rate;
        a._hn++;
      }
      if (r.frequency != null) {
        a._fs += r.frequency;
        a._fn++;
      }
    }
    for (const a of m.values()) {
      a.ctr = a.impressions ? (a.clicks / a.impressions) * 100 : null;
      a.hook = a._hn ? a._hs / a._hn : null;
      a.freq = a._fn ? a._fs / a._fn : null;
    }
    return m;
  }, [metrics]);

  // Unknown/absent currency falls back to the ISO code, never to another
  // tenant's symbol — a Propty number rendered '฿' is a real misreport.
  const sym = currencySymbol(roas?.currency ?? activeOrg?.default_currency);

  const rows: Row[] = useMemo(() => {
    if (!roas) return [];
    // Union of campaigns that either have spend or bookings.
    const ids = new Set<string>();
    roas.campaigns.forEach((c) => ids.add(c.campaign_id));
    attnBy.forEach((_, id) => ids.add(id));
    const byId = new Map(roas.campaigns.map((c) => [c.campaign_id, c]));
    const out: Row[] = [];
    for (const id of ids) {
      const r = byId.get(id);
      const a = attnBy.get(id) ?? null;
      const spend = r?.spend ?? a?.spend ?? 0;
      if (spend <= 0 && (r?.bookings ?? 0) === 0) continue; // nothing to show
      const bookings = r?.bookings ?? 0;
      const revenue = r?.revenue ?? 0;
      const ro = r?.roas ?? (spend > 0 ? revenue / spend : null);
      const d = decide(spend, bookings, ro, a);
      out.push({
        id,
        name: r?.name ?? id,
        spend,
        bookings,
        revenue,
        roas: ro,
        costPerBooking: bookings > 0 ? spend / bookings : null,
        attn: a,
        verdict: d.verdict,
        why: d.why,
      });
    }
    // Order: act-worthy first (SCALE, KILL, ROTATE), then KEEP, then EARLY; by spend.
    const rank: Record<Verdict, number> = { SCALE: 0, KILL: 1, ROTATE: 2, KEEP: 3, EARLY: 4 };
    return out.sort((x, y) => rank[x.verdict] - rank[y.verdict] || y.spend - x.spend);
  }, [roas, attnBy]);

  const loading = !roas && !error;
  const coveragePct = roas?.coverage != null ? Math.round(roas.coverage * 100) : null;
  const blendedRoas =
    roas && roas.total_spend > 0 ? roas.attributed_revenue / roas.total_spend : null;

  return (
    <div style={{ padding: '26px 30px 40px', maxWidth: 1000, animation: 'basilfade .3s ease' }}>
      <PageHeader
        eyebrow="Are your ads producing bookings?"
        title="Results"
        actions={
          <div
            style={{
              display: 'flex',
              border: `1px solid ${C.line}`,
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                style={{
                  padding: '5px 11px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  font: `600 11px 'IBM Plex Mono',monospace`,
                  border: 'none',
                  background: days === d ? C.surface2 : 'transparent',
                  color: days === d ? C.primary : C.muted,
                }}
              >
                {d}D
              </button>
            ))}
          </div>
        }
      />
      {error && <Card style={{ padding: '16px 20px', color: C.act, fontSize: 13 }}>{error}</Card>}
      {loading && (
        <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>LOADING…</div>
      )}

      {/* Traffic & attention — the account-level Meta metrics (moved here from the
          old Overview). Bookings below decide; these diagnose why (RULES §6). */}
      <div style={{ margin: '4px 0 16px' }}>
        <div
          style={{
            font: `600 15px 'Outfit',sans-serif`,
            color: C.primary,
            letterSpacing: '-.01em',
          }}
        >
          Traffic &amp; attention
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
          How your ads hold attention across the whole account — reach, CTR, hook &amp; hold. Use
          these to diagnose <em>why</em> a campaign below is or isn&apos;t booking.
        </div>
      </div>
      <Overview embedded />

      {/* Bookings & revenue. This was the top block, so it never carried a heading;
          now that traffic sits above it, it needs one or it reads as loose cards. */}
      <div style={{ margin: '34px 0 16px' }}>
        <div
          style={{
            font: `600 15px 'Outfit',sans-serif`,
            color: C.primary,
            letterSpacing: '-.01em',
          }}
        >
          Bookings &amp; revenue
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
          The bottom line: what the spend above actually returned, per campaign.
        </div>
      </div>

      {roas && (
        <>
          {/* Bottom line */}
          <Card style={{ padding: '18px 22px', marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 34, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Big
                label="BOOKINGS FROM ADS"
                value={String(roas.campaigns.reduce((s, c) => s + c.bookings, 0))}
              />
              <Big label="ATTRIBUTED REVENUE" value={money(roas.attributed_revenue, sym)} />
              <Big
                label="BLENDED ROAS"
                value={blendedRoas != null ? `${blendedRoas.toFixed(1)}x` : '—'}
                color={blendedRoas != null && blendedRoas >= 1 ? C.good : undefined}
              />
              <Big label="AD SPEND" value={money(roas.total_spend, sym)} />
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 14, lineHeight: 1.5 }}>
              {coveragePct != null ? (
                <>
                  <b style={{ color: C.secondary }}>{coveragePct}%</b> of your booking revenue is
                  traceable to a campaign (via promo code). The rest — WhatsApp, direct, code-less —
                  stays honestly unattributed;{' '}
                  <span
                    onClick={() => navigate('/bookings')}
                    style={{ color: C.basil, cursor: 'pointer', fontWeight: 600 }}
                  >
                    tag those bookings on Bookings →
                  </span>{' '}
                  to raise it.
                </>
              ) : (
                'No bookings synced yet — connect Cloudbeds and sync to see revenue attribution.'
              )}
            </div>
          </Card>

          {/* Per-campaign verdicts */}
          {rows.length === 0 ? (
            <Card style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 13 }}>
              No campaigns with spend in this window yet.
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rows.map((r) => {
                const look = VERDICT_LOOK[r.verdict];
                return (
                  <Card key={r.id} style={{ padding: '14px 18px' }}>
                    <div
                      style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <span
                        style={{
                          font: `700 10px 'IBM Plex Mono',monospace`,
                          letterSpacing: '.08em',
                          color: look.color,
                          border: `1px solid ${alpha(look.color, 45)}`,
                          background: alpha(look.color, 12),
                          borderRadius: 6,
                          padding: '4px 9px',
                          flexShrink: 0,
                        }}
                      >
                        {look.label}
                      </span>
                      {thumbBy[r.id] ? (
                        <img
                          src={thumbBy[r.id]}
                          alt=""
                          onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
                          style={{
                            width: 44,
                            height: 44,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: `1px solid ${C.line}`,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          title="Amplified post — no Basil creative to preview"
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 8,
                            border: `1px solid ${C.lineSoft}`,
                            background: C.surface2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: C.faint,
                            fontSize: 16,
                            flexShrink: 0,
                          }}
                        >
                          ▦
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div
                          style={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: C.primary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {r.name}
                        </div>
                        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{r.why}</div>
                      </div>
                      <Stat label="BOOKINGS" value={String(r.bookings)} />
                      <Stat
                        label="COST / BOOKING"
                        value={r.costPerBooking != null ? money(r.costPerBooking, sym) : '—'}
                      />
                      <Stat
                        label="ROAS"
                        value={r.roas != null ? `${r.roas.toFixed(1)}x` : '—'}
                        color={r.roas != null && r.roas >= 1 ? C.good : undefined}
                      />
                      <Stat label="SPEND" value={money(r.spend, sym)} />
                      <span
                        onClick={() => navigate('/campaigns')}
                        style={{
                          fontSize: 11.5,
                          color: C.basil,
                          cursor: 'pointer',
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        Details →
                      </span>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Big({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div className="label-mono" style={{ marginBottom: 5 }}>
        {label}
      </div>
      <div
        style={{
          font: `700 26px/1 'Outfit',sans-serif`,
          color: color ?? C.primary,
          letterSpacing: '-.02em',
        }}
      >
        {value}
      </div>
    </div>
  );
}
function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ textAlign: 'right', minWidth: 74 }}>
      <div style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint }}>{label}</div>
      <div
        style={{
          font: `600 14px 'Outfit',sans-serif`,
          color: color ?? C.secondary,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 3,
        }}
      >
        {value}
      </div>
    </div>
  );
}
